import React, { useState, useRef } from "react";
import {
  Play, Pause, SpeakerHigh, SpeakerLow, SpeakerSlash,
  SkipBack, SkipForward, Shuffle, Repeat, RepeatOnce, Queue, X,
} from "@phosphor-icons/react";
import { usePlayer } from "../context/PlayerContext";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

function fmt(s) {
  if (!s || !isFinite(s)) return "--:--";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function EqBars() {
  return (
    <div className="absolute inset-0 flex items-end justify-center pb-1 gap-[2px] bg-black/50">
      <span className="w-[3px] rounded-t bg-[#B79C7B] animate-eq1" />
      <span className="w-[3px] rounded-t bg-[#B79C7B] animate-eq2" />
      <span className="w-[3px] rounded-t bg-[#B79C7B] animate-eq3" />
      <span className="w-[3px] rounded-t bg-[#B79C7B] animate-eq2" />
    </div>
  );
}

export default function AudioPlayerBar() {
  const {
    track, isPlaying, progress, duration, toggle, seekTo, volume, setVolume,
    playNext, playPrev, queue, currentIndex,
    shuffle, setShuffle, repeat, cycleRepeat,
  } = usePlayer();

  const [showQueue, setShowQueue] = useState(false);
  const progressRef = useRef(null);

  if (!track) return null;

  const hasQueue = queue.length > 1;

  const onScrub = (e) => {
    const rect = progressRef.current?.getBoundingClientRect();
    if (!rect) return;
    const r = (e.clientX - rect.left) / rect.width;
    seekTo(Math.max(0, Math.min(1, r)));
  };

  const toggleMute = () => setVolume(volume > 0 ? 0 : 0.85);

  return (
    <>
      {/* Queue panel */}
      <AnimatePresence>
        {showQueue && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-[84px] right-4 z-50 w-80 glass-strong border border-white/10 shadow-2xl overflow-hidden"
            data-testid="queue-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
              <span className="overline text-[#B79C7B]">
                Queue &mdash; {queue.length} track{queue.length !== 1 ? "s" : ""}
              </span>
              <button onClick={() => setShowQueue(false)} className="text-[#71717A] hover:text-ivory transition-colors">
                <X size={14} />
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto scrollbar-thin">
              {queue.map((t, i) => (
                <div
                  key={t.id}
                  className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
                    i === currentIndex ? "bg-[#8A2E2E]/20 border-l-2 border-[#8A2E2E]" : "hover:bg-white/4"
                  }`}
                >
                  <div className="w-9 h-9 shrink-0 relative overflow-hidden bg-[#1A1A1D]">
                    {t.thumbnail && <img src={t.thumbnail} alt="" className="w-full h-full object-cover" />}
                    {i === currentIndex && isPlaying && <EqBars />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-xs truncate font-medium ${i === currentIndex ? "text-[#D4B896]" : "text-ivory"}`}>
                      {t.title}
                    </div>
                    <div className="text-[10px] text-[#71717A] truncate">{t.reciter_name}</div>
                  </div>
                  {i === currentIndex && (
                    <div className="w-1.5 h-1.5 rounded-full bg-[#B79C7B] shrink-0 animate-pulseLive" />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Player bar */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/8"
        style={{ background: "linear-gradient(to top, #0A0A0B 0%, rgba(12,12,14,0.97) 100%)", backdropFilter: "blur(32px)" }}
        data-testid="audio-player-bar"
      >
        {/* Scrubber */}
        <div
          ref={progressRef}
          onClick={onScrub}
          className="relative h-[3px] bg-[#1E1E22] cursor-pointer group"
          data-testid="player-progress"
        >
          <div
            className="absolute inset-y-0 left-0 transition-none"
            style={{
              width: `${progress * 100}%`,
              background: "linear-gradient(to right, #8A2E2E, #B79C7B)",
            }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#D4B896] border-2 border-[#0A0A0B] shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ left: `calc(${progress * 100}% - 6px)` }}
          />
        </div>

        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 h-[76px] grid grid-cols-[1fr_auto_1fr] items-center gap-4">

          {/* Left — track info */}
          <Link
            to={`/track/${track.id}`}
            className="flex items-center gap-3 min-w-0"
            data-testid="player-track-link"
          >
            <div className="w-12 h-12 shrink-0 relative overflow-hidden border border-white/10 bg-[#1A1A1D]">
              {track.thumbnail && <img src={track.thumbnail} alt="" className="w-full h-full object-cover" />}
              {isPlaying && <EqBars />}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-ivory leading-tight">{track.title}</div>
              <div className="truncate text-xs text-[#A1A1AA] mt-0.5">{track.reciter_name}</div>
            </div>
          </Link>

          {/* Center — controls */}
          <div className="flex items-center gap-2 lg:gap-3">
            <button
              onClick={() => setShuffle(!shuffle)}
              title="Shuffle"
              className={`p-2 rounded-full transition-all ${
                shuffle ? "text-[#B79C7B] bg-[#B79C7B]/10" : "text-[#71717A] hover:text-[#A1A1AA]"
              }`}
            >
              <Shuffle size={15} weight={shuffle ? "fill" : "regular"} />
            </button>

            <button
              onClick={playPrev}
              title="Previous"
              className="p-2 text-[#A1A1AA] hover:text-ivory transition-colors"
              data-testid="player-prev"
            >
              <SkipBack size={20} weight="fill" />
            </button>

            <button
              onClick={toggle}
              data-testid="player-toggle-button"
              className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-105 active:scale-95"
              style={{
                background: "linear-gradient(135deg, #A33B3B 0%, #8A2E2E 60%, #6B2020 100%)",
                boxShadow: isPlaying
                  ? "0 0 0 3px rgba(138,46,46,0.3), 0 0 24px rgba(138,46,46,0.5)"
                  : "0 0 16px rgba(138,46,46,0.4)",
              }}
            >
              {isPlaying
                ? <Pause size={18} weight="fill" className="text-ivory" />
                : <Play size={18} weight="fill" className="text-ivory ml-0.5" />}
            </button>

            <button
              onClick={playNext}
              title="Next"
              className="p-2 text-[#A1A1AA] hover:text-ivory transition-colors"
              data-testid="player-next"
            >
              <SkipForward size={20} weight="fill" />
            </button>

            <button
              onClick={cycleRepeat}
              title={repeat === "none" ? "Repeat off" : repeat === "all" ? "Repeat all" : "Repeat one"}
              className={`p-2 rounded-full transition-all ${
                repeat !== "none" ? "text-[#B79C7B] bg-[#B79C7B]/10" : "text-[#71717A] hover:text-[#A1A1AA]"
              }`}
            >
              {repeat === "one"
                ? <RepeatOnce size={15} weight="fill" />
                : <Repeat size={15} weight={repeat === "all" ? "fill" : "regular"} />}
            </button>
          </div>

          {/* Right — time, volume, queue */}
          <div className="flex items-center justify-end gap-3">
            <span className="hidden md:block text-[10px] text-[#71717A] font-mono whitespace-nowrap tabular-nums">
              {fmt(progress * duration)} / {fmt(duration)}
            </span>

            <div className="hidden md:flex items-center gap-2">
              <button onClick={toggleMute} className="text-[#71717A] hover:text-[#A1A1AA] transition-colors">
                {volume === 0
                  ? <SpeakerSlash size={15} />
                  : volume > 0.5
                  ? <SpeakerHigh size={15} />
                  : <SpeakerLow size={15} />}
              </button>
              <input
                type="range" min={0} max={1} step={0.01} value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="w-20 accent-[#B79C7B] cursor-pointer"
                data-testid="player-volume"
              />
            </div>

            {hasQueue && (
              <button
                onClick={() => setShowQueue((v) => !v)}
                title="Queue"
                className={`p-2 rounded-full transition-all ${
                  showQueue ? "text-[#B79C7B] bg-[#B79C7B]/10" : "text-[#71717A] hover:text-[#A1A1AA]"
                }`}
                data-testid="player-queue-btn"
              >
                <Queue size={15} weight={showQueue ? "fill" : "regular"} />
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
